/**
 * Appliers barrel for @cloudnest/cascadia-engine.
 *
 * `dispatchApplier` is the top-level discriminator that routes an `Action`
 * to the correct applier function.
 *
 * Init is special: the dispatcher accepts `state: GameState | null` for it.
 * All other actions require a non-null state.
 */

import type { Action } from "../types/actions";
import type { GameState } from "../types/game-state";
import { applyChooseLandmarkCard } from "./choose-landmark-card.applier";
import { applyDraftDecoupled } from "./draft-decoupled.applier";
import { applyDraftPair } from "./draft-pair.applier";
import { applyEndTurn } from "./end-turn.applier";
import { applyInit } from "./init.applier";
import { applyPlaceTile } from "./place-tile.applier";
import { applyPlaceWildlife } from "./place-wildlife.applier";
import { applyReturnWildlife } from "./return-wildlife.applier";
import { applySkipLandmark } from "./skip-landmark.applier";
import { applySpendNatureDecouple } from "./spend-nature-decouple.applier";
import { applySpendNatureWipe } from "./spend-nature-wipe.applier";
import { applyUnlockLandmark } from "./unlock-landmark.applier";
import { applyWipeMarketOptional } from "./wipe-market-optional.applier";

export { applyChooseLandmarkCard } from "./choose-landmark-card.applier";
export { bumpVersion } from "./common-applier";
export { applyDraftDecoupled } from "./draft-decoupled.applier";
export { applyDraftPair } from "./draft-pair.applier";
export { applyEndTurn } from "./end-turn.applier";
export { applyInit } from "./init.applier";
export { applyPlaceTile } from "./place-tile.applier";
export { applyPlaceWildlife } from "./place-wildlife.applier";
export { applyReturnWildlife } from "./return-wildlife.applier";
export { applySkipLandmark } from "./skip-landmark.applier";
export { applySpendNatureDecouple } from "./spend-nature-decouple.applier";
export { applySpendNatureWipe } from "./spend-nature-wipe.applier";
export { applyUnlockLandmark } from "./unlock-landmark.applier";
export { applyWipeMarketOptional } from "./wipe-market-optional.applier";

/**
 * Top-level applier dispatcher. Routes `action` to the correct applier.
 *
 * For `Init`, `state` may be `null`. For all other actions, `state` must be
 * a valid `GameState`. Passing `null` for non-Init actions throws.
 */
export const dispatchApplier = (
  state: GameState | null,
  action: Action,
): GameState => {
  switch (action.type) {
    case "Init":
      return applyInit(state, action);
    case "DraftPair":
      return applyDraftPair(state!, action);
    case "DraftDecoupled":
      return applyDraftDecoupled(state!, action);
    case "SpendNatureDecouple":
      return applySpendNatureDecouple(state!, action);
    case "SpendNatureWipe":
      return applySpendNatureWipe(state!, action);
    case "WipeMarketOptional":
      return applyWipeMarketOptional(state!, action);
    case "PlaceTile":
      return applyPlaceTile(state!, action);
    case "PlaceWildlife":
      return applyPlaceWildlife(state!, action);
    case "ReturnWildlife":
      return applyReturnWildlife(state!, action);
    case "UnlockLandmark":
      return applyUnlockLandmark(state!, action);
    case "ChooseLandmarkCard":
      return applyChooseLandmarkCard(state!, action);
    case "SkipLandmark":
      return applySkipLandmark(state!, action);
    case "EndTurn":
      return applyEndTurn(state!, action);
  }
};
